import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core'
import { shareReplay, map, take, tap, Subject, Observable, BehaviorSubject } from 'rxjs'
import { ConferencesDataService } from '../conferences-data.service'
import type { MessageModel, SEOMetadata } from '@flocasts/experience-service-types'
import { isMessageModel, isMultipleSectionsViewModel } from '../../shared/functions/type-guards'
import { ListPageProperties, SegmentTagV2 } from 'src/app/shared/analytics/models/segment-tag.model'
import { SegmentService } from 'src/app/shared/analytics/services/segment.service'
import {
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  PageMetaService
} from 'src/app/libs/page-meta/page-meta.service'

export const CONFERENCE_LIST_SEGMENT_TAG: SegmentTagV2 = {
  autoPageCall: false,
  category: 'Conferences',
  page_category: 'Conferences'
}

@Component({
  selector: 'flo-conference-list',
  templateUrl: './conference-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConferenceListComponent implements OnDestroy {
  constructor(
    private readonly conferencesDataService: ConferencesDataService,
    private readonly segmentService: SegmentService,
    private readonly pageMetaService: PageMetaService
  ) {}

  private readonly ngOnDestroy$ = new Subject<void>()

  // if layout service fails, display error
  public readonly isLayoutError$ = new BehaviorSubject<boolean>(false)

  public conferenceLayout$ = this.conferencesDataService.getConferencesList().pipe(
    tap(layout => {
      !layout ? this.isLayoutError$.next(true) : this.isLayoutError$.next(false)
    }),
    shareReplay(1)
  )

  private setPageMeta(seo: SEOMetadata): void {
    this.pageMetaService.setPageMeta({
      type: 'website',
      twitterCard: 'summary_large_image',
      title: seo.title || 'Conferences',
      description: seo.description || '',
      canonicalUrl: seo.canonicalUrl || '',
      siteName: null,
      image: {
        url: seo.imageUrl || '',
        height: Number(seo.imageHeight) || DEFAULT_OG_IMAGE_HEIGHT,
        width: Number(seo.imageWidth) || DEFAULT_OG_IMAGE_WIDTH
      }
    })
  }

  private seoPageMeta$ = this.conferenceLayout$
    .pipe(
      take(1),
      map(res => res?.seo),
      tap(seo => {
        if (!!seo) {
          this.setPageMeta(seo)
        }
      })
    )
    .subscribe() // unsubscribed by take(1)

  private analyticsPageCall$ = this.conferenceLayout$
    .pipe(
      take(1),
      map(res => res?.analytics),
      tap(analytics => {
        const segmentTag: SegmentTagV2 = {
          ...CONFERENCE_LIST_SEGMENT_TAG,
          page_category: analytics?.extraContext?.pageCategory || 'Conferences'
        }
        const segmentPageProperties: ListPageProperties = { _tag: 'list' }
        this.segmentService.pageV2(segmentTag, segmentPageProperties)
      })
    )
    .subscribe() // unsubscribed by take(1)

  public message$: Observable<MessageModel | undefined> = this.conferenceLayout$.pipe(
    map(res => {
      if (res && isMultipleSectionsViewModel(res)) {
        return res.sections.find(isMessageModel)
      }
      return
    })
  )

  public ngOnDestroy() {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
    this.isLayoutError$.complete()
    this.pageMetaService.clearMeta()
  }
}
